import React, { useCallback } from 'react';
import { Edge } from 'reactflow';
import useFlowsStore from '../../../store/storeFlows';
import { nodeDataAutoSaveDynamic } from '../../../util/autosave/nodedata_autosave';
import {
  enrichMergedWithObjectDefaults,
  serializeNodeToBlobConfig,
} from '../nodeConfigUtils';
import type { ThirdPartyMode } from '../types';

export function useThirdPartyPersist(
  edges: Edge[],
  id: string,
  flowId: string | undefined,
  setNodeState: (value: unknown) => void,
  setSaving: (saving: {
    lastSaved?: string;
    status: string;
    finished?: boolean;
    flowId?: string;
  }) => void,
  setSchema: (
    schema: Record<string, { type: string; required: boolean }>,
  ) => void,
  setBlobConfig: (value: string) => void,
) {
  const persist = useCallback(
    (payload: Record<string, unknown>) => {
      if (useFlowsStore.getState().flowCanvasPhase !== 'ready') {
        return;
      }
      nodeDataAutoSaveDynamic({
        newEdges: edges,
        setNodeState,
        id,
        flowId,
        setSaving,
        objectData: payload,
        setSchema,
      });
    },
    [edges, setNodeState, id, flowId, setSaving, setSchema],
  );

  const persistWithBlob = useCallback(
    (
      merged: Record<string, unknown>,
      provider: string,
      mode: ThirdPartyMode,
      _payload: Record<string, unknown>,
    ) => {
      if (useFlowsStore.getState().flowCanvasPhase !== 'ready') {
        return;
      }
      const enriched = enrichMergedWithObjectDefaults(merged, provider, mode);
      const blobStr = serializeNodeToBlobConfig(enriched);
      setBlobConfig(blobStr);
      persist({ ...enriched, blobConfig: blobStr });
    },
    [persist, setBlobConfig],
  );

  return { persist, persistWithBlob };
}
